import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { faClipboardListCheck } from '@fortawesome/pro-regular-svg-icons/faClipboardListCheck';
import { faClipboardUser } from '@fortawesome/pro-regular-svg-icons/faClipboardUser';
import { Typography } from 'antd';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { PosCard } from 'pos-client/components/shared/Display/Card/PosCard.tsx';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

import { WorkshopJobsTable } from './WorkshopJobsTable.tsx';

interface WorkshopJobsProps {
  jobs: ScheduleBoardJobModel[];
  isLoading: boolean;
}

export const WorkshopJobs = ({ jobs, isLoading }: WorkshopJobsProps) => {
  const { t } = useTranslation();
  const { selectedLocation } = useScheduleBoardContext();

  const authorisedJobs = useMemo(
    () =>
      jobs
        .map(({ orderReference, ...rest }) => ({
          ...rest,
          orderReference: selectedLocation?.code
            ? (orderReference?.slice(
                orderReference.indexOf(selectedLocation?.code ?? '') +
                  selectedLocation?.code?.length +
                  1
              ) ?? null)
            : orderReference,
        }))
        .sort((a, b) => (a.startDateDisplay > b.startDateDisplay ? 1 : -1)),
    [jobs, selectedLocation?.code]
  );

  const activeJobs = authorisedJobs.filter(({ state }) => state !== 'complete');
  const completedJobs = authorisedJobs.filter(
    ({ state }) => state === 'complete'
  );

  return (
    <PosGrid>
      <PosCard
        headerProps={{
          title: t('workshop.workshopJobs'),
          icon: faClipboardUser,
        }}
        colProps={{
          span: 24,
        }}
      >
        <WorkshopJobsTable jobs={activeJobs} isLoading={isLoading} />
      </PosCard>
      <PosCard
        headerProps={{
          title: t('workshop.workshopCompletedJobs'),
          icon: faClipboardListCheck,
          action: (
            <Typography.Title
              level={4}
              style={{ margin: 0 }}
            >{`${completedJobs.length}/${authorisedJobs.length} ${t('jobs.jobsCompleted')}`}</Typography.Title>
          ),
        }}
        colProps={{
          span: 24,
        }}
      >
        <WorkshopJobsTable jobs={completedJobs} isLoading={isLoading} />
      </PosCard>
    </PosGrid>
  );
};
